/**
 * Operator "Attention" criteria for screens (epic #461 / issue #467).
 *
 * A screen needs attention when any rule matches:
 * 1. **Unstable connection** — online but WebSocket degraded to HTTP polling.
 * 2. **Stale while online** — marked online but no heartbeat within the active window.
 * 3. **Recently offline** — offline after contacting the platform within the recent-offline window.
 * 4. **Low 7-day uptime** — disabled (epic #2311 / #2314): `uptimePercent` is the calendar
 *    24h-UTC-clock metric, and vehicle fleets legitimately run well below
 *    {@link PLAYER_ATTENTION_UPTIME_THRESHOLD_PCT} on that basis alone. Re-enable once
 *    sessionFill (T4–T6, #2315–#2317) is available to gate on instead.
 */
export const PLAYER_ATTENTION_STALE_HEARTBEAT_MS = 5 * 60_000
export const PLAYER_ATTENTION_RECENT_OFFLINE_MS = 5 * 60_000
export const PLAYER_ATTENTION_UPTIME_THRESHOLD_PCT = 90

export const PLAYER_ATTENTION_REASONS = [
  'unstable_connection',
  'stale_while_online',
  'recently_offline',
  'low_uptime_7d'
] as const

export type TPlayerAttentionReason = (typeof PLAYER_ATTENTION_REASONS)[number]

export interface TPlayerInAttentionInput {
  online?: boolean | null
  pooling?: boolean | null
  lastSeen?: Date | string | number | null
  uptimePercent?: number | null
}

function lastSeenToMs(lastSeen: TPlayerInAttentionInput['lastSeen']): number | null {
  if (lastSeen == null) {
    return null
  }
  const t = lastSeen instanceof Date ? lastSeen.getTime() : new Date(lastSeen).getTime()
  return Number.isFinite(t) ? t : null
}

export function isPlayerRecentlyOffline(
  player: Pick<TPlayerInAttentionInput, 'online' | 'lastSeen'>,
  nowMs: number = Date.now()
): boolean {
  if (player.online === true) {
    return false
  }
  const seenMs = lastSeenToMs(player.lastSeen)
  if (seenMs == null) {
    return false
  }
  return nowMs - seenMs <= PLAYER_ATTENTION_RECENT_OFFLINE_MS
}

export function getPlayerAttentionReasons(
  player: TPlayerInAttentionInput,
  nowMs: number = Date.now()
): TPlayerAttentionReason[] {
  const reasons: TPlayerAttentionReason[] = []

  if (player.online === true && player.pooling === true) {
    reasons.push('unstable_connection')
  }

  if (player.online === true && player.pooling !== true) {
    const seenMs = lastSeenToMs(player.lastSeen)
    if (seenMs != null && nowMs - seenMs > PLAYER_ATTENTION_STALE_HEARTBEAT_MS) {
      reasons.push('stale_while_online')
    }
  }

  if (isPlayerRecentlyOffline(player, nowMs)) {
    reasons.push('recently_offline')
  }

  // low_uptime_7d is disabled — see the module doc comment above (#2314).

  return reasons
}

export function isPlayerInAttention(
  player: TPlayerInAttentionInput,
  nowMs: number = Date.now()
): boolean {
  return getPlayerAttentionReasons(player, nowMs).length > 0
}

/** Per-reason hit counts across a fleet (a screen may increment more than one reason). */
export function countPlayerAttentionReasons(
  players: readonly TPlayerInAttentionInput[],
  nowMs: number = Date.now()
): Record<TPlayerAttentionReason, number> {
  const counts = Object.fromEntries(
    PLAYER_ATTENTION_REASONS.map(reason => [reason, 0])
  ) as Record<TPlayerAttentionReason, number>
  for (const player of players) {
    for (const reason of getPlayerAttentionReasons(player, nowMs)) {
      counts[reason] += 1
    }
  }
  return counts
}

/**
 * Most common attention reason among players that need attention.
 * Returns null when none need attention.
 */
export function dominantPlayerAttentionReason(
  players: readonly TPlayerInAttentionInput[],
  nowMs: number = Date.now()
): TPlayerAttentionReason | null {
  const counts = countPlayerAttentionReasons(players, nowMs)
  let best: TPlayerAttentionReason | null = null
  let bestN = 0
  for (const reason of PLAYER_ATTENTION_REASONS) {
    if (counts[reason] > bestN) {
      bestN = counts[reason]
      best = reason
    }
  }
  return bestN > 0 ? best : null
}
